import { Button } from "common/ui/button"
import { Spinner } from "common/ui/spinner"
import { Check, RefreshCw, RotateCcw, RotateCw } from "lucide-react"
import { useEffect, useState } from "react"

interface ImageEditorProps {
  onClose: () => void
  imageUrl: string
  onSave: (imageFile: File, previewUrl: string) => void
  isLocalImage?: boolean // Indicates if image is from our server
}

export default function ImageEditor({ 
  onClose = () => {}, 
  imageUrl,
  onSave,
  isLocalImage = false
}: ImageEditorProps) {

  const [rotation, setRotation] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  
  // Load image
  useEffect(() => {
    if (!imageUrl) return;
    
    setIsLoading(true);
    setErrorMessage(null);
    
    const img = new Image();
    
    if (!isLocalImage && imageUrl.startsWith('http')) {
      img.crossOrigin = "anonymous";
    }
    
    img.onload = () => setIsLoading(false);
    img.onerror = () => {
      setErrorMessage("Failed to load image");
      setIsLoading(false);
    };
    
    img.src = imageUrl;
  }, [imageUrl, isLocalImage]);

  // Simple rotation functions
  const rotate = (direction: 'left' | 'right') => {
    setRotation(prev => {
      const change = direction === 'left' ? -90 : 90;
      const newRotation = (prev + change) % 360;
      return newRotation < 0 ? newRotation + 360 : newRotation;
    });
  };

  // Only process the image when actually saving
  const handleSave = async () => {
    if (!imageUrl) return;
    const confirmSave = window.confirm("Are you sure you want to save these changes?");
    if (!confirmSave) return;
  
    try {
      setIsLoading(true);
      setErrorMessage(null);
      
      // Create a new image from the source
      const image = await createImage(imageUrl);
      
      // Create a canvas for the rotated image
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("No 2d context");
  
      // Set dimensions based on rotation
      const swapDimensions = Math.abs(rotation % 180) === 90;
      canvas.width = swapDimensions ? image.height : image.width;
      canvas.height = swapDimensions ? image.width : image.height;
  
      // Apply rotation transformation
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.drawImage(
        image, 
        -image.width / 2, 
        -image.height / 2, 
        image.width, 
        image.height
      );
  
      // Convert to blob and create File
      canvas.toBlob(blob => {
        if (!blob) {
          setErrorMessage("Failed to create image blob");
          return;
        }
        
        const blobType = blob.type || 'image/jpeg';
        const fileExtension = blobType.split('/')[1] || 'jpg';
        const filename = `edited-image-${new Date().getTime()}.${fileExtension}`;
        
        onSave(
          new File([blob], filename, { type: blobType }),
          URL.createObjectURL(blob)
        );
      });
    } catch (e) {
      console.error('Error saving image:', e);
      setErrorMessage("Error saving the edited image");
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to create an image
  const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const image = new Image();
      
      if (!isLocalImage && url.startsWith('http')) {
        image.crossOrigin = "anonymous";
      }
      
      image.addEventListener("load", () => resolve(image));
      image.addEventListener("error", (error) => reject(error));
      image.src = url;
    });

  return (
    <>
      {errorMessage && (
        <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-2 rounded mb-4">
          {errorMessage}
        </div>
      )}

      <div className="relative h-[400px] w-full overflow-hidden">
 
          <div className="h-full w-full bg-gray-100 dark:bg-gray-800 relative flex items-center justify-center">
            <img 
              src={imageUrl}
              alt="Image to be rotated"
              className="max-w-full max-h-full object-contain"
              style={{ transform: `rotate(${rotation}deg)` }}
            />
          </div>
  
      </div>

      <div className="mt-4 flex justify-between items-center">
        <Button variant="outline" size="sm" onClick={() => setRotation(0)}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Reset
        </Button>
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="icon" onClick={() => rotate('left')}>
            <RotateCcw className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="icon" onClick={() => rotate('right')}>
            <RotateCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="mt-4 flex justify-end space-x-2">
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          disabled={isLoading}
        >
          {isLoading ? (
            <Spinner className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Check className="mr-2 h-4 w-4" />
          )}
          Save Changes
        </Button>
      </div>
    </>
  );
}